import { useState } from 'react';
import { Link } from 'react-router-dom';

import { LoadingSpinner } from '../../../../components/LoadingSpinner';
import { useApplyRecommendation, useDismissRecommendation, useImprovementSignals } from '../hooks';
import type { ImprovementRecommendationResponse } from '../api';

interface ImprovementSignalsPanelProps {
  slug: string;
}

function signalTypeLabel(type: string): string {
  switch (type) {
    case 'LowConfidence':
      return 'Confianza baja';
    case 'FrequentCorrection':
      return 'Correcciones frecuentes';
    case 'MissingExpected':
      return 'Campo esperado ausente';
    case 'UnexpectedExtra':
      return 'Campo extra inesperado';
    case 'OcrTierEscalation':
      return 'Nivel OCR insuficiente';
    default:
      return type;
  }
}

/** Renders the actionable card for a type-level OCR-tier escalation recommendation. */
function OcrTierEscalationCard({
  item,
  slug,
}: {
  item: ImprovementRecommendationResponse;
  slug: string;
}): JSX.Element {
  return (
    <li
      key="ocr-tier-escalation"
      className="card border-blue-200 bg-blue-50 p-4"
      data-testid="ocr-tier-escalation-card"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold text-blue-900">Tipo de documento</span>
        <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
          Nivel OCR insuficiente
        </span>
        <span className="text-xs tabular-nums text-slate-500">
          {item.occurrenceCount} evidencia(s)
        </span>
      </div>
      <p className="mt-2 text-sm text-blue-900">{item.message}</p>
      <p className="mt-1 text-sm text-slate-600">
        <span className="font-medium text-slate-700">Acción propuesta:</span>{' '}
        {item.proposedAction}
      </p>
      <div className="mt-3">
        <Link
          to={`/document-types/${encodeURIComponent(slug)}`}
          className="inline-flex items-center rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          data-testid="ocr-tier-escalation-action-link"
        >
          Abrir editor del tipo →
        </Link>
      </div>
    </li>
  );
}

/**
 * Panel that surfaces aggregated improvement signals for a published document
 * type. Field-level recommendations can be applied (sets a reviewed hint on the
 * field, closing the auto-learning loop) or ignored. The OCR-tier escalation card
 * stays a link to the editor. Empty list → empty-state card.
 */
export function ImprovementSignalsPanel({ slug }: ImprovementSignalsPanelProps): JSX.Element {
  const { data, isLoading, isError } = useImprovementSignals(slug);
  const applyMutation = useApplyRecommendation();
  const dismissMutation = useDismissRecommendation();
  const [applyTarget, setApplyTarget] = useState<ImprovementRecommendationResponse | null>(null);
  const [hint, setHint] = useState('');

  if (isLoading) {
    return (
      <div className="flex justify-center py-6">
        <LoadingSpinner />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="card border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
        No se pudieron cargar las recomendaciones.
      </div>
    );
  }

  const items = data ?? [];
  if (items.length === 0) {
    return (
      <div className="card p-6 text-center text-sm text-slate-500">
        No hay señales activas para este tipo. Cuando los revisores corrijan
        documentos, las sugerencias aparecerán aquí.
      </div>
    );
  }

  function openApply(item: ImprovementRecommendationResponse): void {
    setApplyTarget(item);
    // Pre-fill with the schema-derived suggestion so the admin starts from a concrete hint
    // (editable) instead of a blank box.
    setHint(item.proposedHint);
  }

  function confirmApply(): void {
    if (applyTarget === null || hint.trim() === '') {
      return;
    }
    applyMutation.mutate(
      { slug, signalId: applyTarget.signalId, hint: hint.trim() },
      { onSuccess: () => setApplyTarget(null) },
    );
  }

  return (
    <>
      <ul className="space-y-3" data-testid="signals-list">
        {items.map((item) => {
          if (item.signalType === 'OcrTierEscalation') {
            return (
              <OcrTierEscalationCard
                key={`ocr-tier-escalation-${item.documentTypeSlug}`}
                item={item}
                slug={slug}
              />
            );
          }

          return (
            <li
              key={item.signalId !== '' ? item.signalId : `${item.fieldName}-${item.signalType}`}
              className="card p-4"
              data-testid="signal-item"
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold text-slate-900">{item.fieldName}</span>
                <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                  {signalTypeLabel(item.signalType)}
                </span>
                <span className="text-xs tabular-nums text-slate-500">
                  {item.occurrenceCount} ocurrencia(s)
                </span>
              </div>
              <p className="mt-2 text-sm text-slate-700">{item.message}</p>
              <p className="mt-1 text-sm text-slate-500">
                <span className="font-medium text-slate-600">Acción propuesta:</span>{' '}
                {item.proposedAction}
              </p>
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  onClick={() => openApply(item)}
                  className="inline-flex items-center rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="apply-recommendation"
                >
                  Aplicar sugerencia
                </button>
                <button
                  type="button"
                  onClick={() => dismissMutation.mutate({ slug, signalId: item.signalId })}
                  disabled={dismissMutation.isPending}
                  className="inline-flex items-center rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  data-testid="dismiss-recommendation"
                >
                  Ignorar
                </button>
              </div>
            </li>
          );
        })}
      </ul>

      {applyTarget !== null ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="card w-full max-w-lg p-5">
            <h3 className="text-base font-semibold text-slate-900">
              Mejorar el campo «{applyTarget.fieldName}»
            </h3>
            <p className="mt-1 text-sm text-slate-600">{applyTarget.message}</p>
            {applyTarget.proposedKind === 'Pattern' ? (
              <p
                className="mt-2 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800"
                data-testid="apply-pattern-note"
              >
                Este campo falla por su patrón (regex). El hint es un parche rápido; para una solución
                definitiva, revisa el patrón del campo en el editor del esquema.
              </p>
            ) : null}
            <label className="label mt-4 block" htmlFor="apply-hint">
              Hint para guiar la extracción
            </label>
            <textarea
              id="apply-hint"
              className="input"
              rows={4}
              value={hint}
              onChange={(e) => setHint(e.target.value)}
              placeholder="Ej.: el IBAN aparece en la cabecera, junto al logo del banco."
              disabled={applyMutation.isPending}
            />
            {applyMutation.isError ? (
              <p className="mt-2 text-xs text-rose-700">
                {applyMutation.error instanceof Error ? applyMutation.error.message : 'No se pudo aplicar.'}
              </p>
            ) : null}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setApplyTarget(null)}
                disabled={applyMutation.isPending}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={confirmApply}
                disabled={applyMutation.isPending || hint.trim() === ''}
                data-testid="confirm-apply"
              >
                {applyMutation.isPending ? 'Aplicando…' : 'Aplicar'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
